/**
 * API Error Classes
 *
 * Custom error classes for API error handling
 */

/**
 * Base API error class
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * Check if error is a client error (4xx)
   */
  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if error is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Check if error is an authentication error
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Check if error is a forbidden error
   */
  isForbiddenError(): boolean {
    return this.status === 403;
  }

  /**
   * Check if error is a not found error
   */
  isNotFoundError(): boolean {
    return this.status === 404;
  }

  /**
   * Check if error is a validation error
   */
  isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

/**
 * Network error (no connection, timeout, etc.)
 */
export class NetworkError extends Error {
  public readonly isNetworkError = true;
  public readonly originalError?: Error;

  constructor(message: string, originalError?: Error) {
    super(message);
    this.name = 'NetworkError';
    this.originalError = originalError;
  }
}

/**
 * Timeout error
 */
export class TimeoutError extends Error {
  public readonly isTimeoutError = true;
  public readonly timeout: number;

  constructor(message: string, timeout: number) {
    super(message);
    this.name = 'TimeoutError';
    this.timeout = timeout;
  }
}

/**
 * Check if an error is a retryable error
 */
export function isRetryableError(error: unknown): boolean {
  // Network errors are retryable
  if (error instanceof NetworkError) {
    return true;
  }

  // Timeout errors are retryable
  if (error instanceof TimeoutError) {
    return true;
  }

  // Server errors (5xx) are retryable
  if (error instanceof ApiError && error.isServerError()) {
    return true;
  }

  // TypeErrors from fetch are usually network errors
  if (error instanceof TypeError) {
    return true;
  }

  return false;
}

/**
 * Parse error response from API
 */
export async function parseErrorResponse(
  response: Response
): Promise<{ message: string; code?: string; details?: Record<string, unknown> }> {
  try {
    const data = await response.json();
    return {
      message: data.message || `Request failed with status ${response.status}`,
      code: data.code,
      details: data.details,
    };
  } catch {
    return {
      message: `Request failed with status ${response.status}`,
    };
  }
}
